import { parseISO } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import { Image } from 'expo-image';
import { router } from 'expo-router';
import React from 'react';
import { Dimensions, Platform, ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Reservation } from '@/api/reservations/types';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import {
  RentalTermsDetails,
  RentalTermsSummary,
} from '@/components/rental-terms';
import {
  CAR_CONDITIONS_CHECKBOX_TEXT,
  DRIVER_INFO_NOTICE,
  FINANCIAL_RESPONSIBILITY_PARAGRAPHS,
} from '@/components/rental-terms/copy';
import { SignedSignaturesSection } from '@/components/signed-signatures';
import {
  formatNumber,
  getCarImages,
  isDesktop,
  openAddressInMaps,
  snakeToTitleCase,
} from '@/core';
import { PINKLING } from '@/style/colors';
import { Button, Card, Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';

const PAGE_WIDTH = Dimensions.get('window').width;

type Props = {
  resData: Reservation;
  resId: string;
  showBackButton?: boolean;
  onDownloadPdf?: () => Promise<void>;
  isGeneratingPdf?: boolean;
};

export function RentalAgreementView({
  resData,
  resId,
  showBackButton = true,
  onDownloadPdf,
  isGeneratingPdf = false,
}: Props) {
  const { styles, theme } = useStyles(stylesheet);

  // For day-by-day (daily-cadence) billing the trip's numDays spans the whole
  // reservation (start→end), but only the days that actually have a reservation
  // rate are billed. Sum the rate days so per-day counts/costs reflect what's
  // billed. Falls back to numDays when there are no rates.
  const billedDays =
    resData.reservationRates?.reduce((sum, rate) => sum + rate.numDays, 0) ||
    resData.numDays;

  return (
    <ScrollView
      contentContainerStyle={{ flexGrow: 1, gap: 32, alignItems: 'center' }}
      style={{ backgroundColor: theme.colors.background_1 }}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.container}>
        {showBackButton &&
        ['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => router.back()}
            />
          </View>
        ) : null}

        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          style={styles.scrollView}
        >
          <View style={{ flexGrow: 1, gap: 32 }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <View style={{ gap: 16, flexShrink: 1 }}>
                <Text
                  tx="rentalAgreement.rentalAgreement"
                  values={{ id: resId }}
                  type="largeTitle"
                />
              </View>

              {onDownloadPdf && isDesktop() && Platform.OS === 'web' ? (
                <View>
                  <Button
                    type="secondarySmall"
                    text={isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                    loading={isGeneratingPdf}
                    disabled={isGeneratingPdf}
                    onPress={onDownloadPdf}
                  />
                </View>
              ) : null}
            </View>

            <RentalTermsSummary />

            <View style={styles.rentalBreakdown}>
              <View style={styles.rentalInformation}>
                <View>
                  <Card>
                    <View
                      style={{
                        borderBottomColor:
                          theme.colors.card.default.borderColor,
                        borderBottomWidth: 1,
                      }}
                    >
                      <View style={{ padding: 16 }}>
                        <Text type="title2">Rental Information</Text>
                      </View>
                    </View>

                    <View
                      style={{
                        borderBottomColor:
                          theme.colors.card.default.borderColor,
                        borderBottomWidth: 1,
                      }}
                    >
                      <View style={{ padding: 16, gap: 4 }}>
                        <View>
                          <Text type="bodyBold">
                            {resData.customPickupLocation
                              ? 'Dropoff location'
                              : 'Pickup location'}
                          </Text>

                          <Button
                            type="link"
                            onPress={() =>
                              openAddressInMaps(
                                resData.customPickupLocation ||
                                  resData.pickupLocation.address
                              )
                            }
                            style={{
                              width: '100%',
                              height: 'auto',
                              paddingTop: 0,
                              paddingRight: 0,
                              paddingBottom: 0,
                              paddingLeft: 0,
                              alignItems: 'flex-start',
                              justifyContent: 'flex-start',
                            }}
                          >
                            <Text
                              type="bodyRegular"
                              style={{ textDecorationLine: 'underline' }}
                            >
                              {resData.customPickupLocation ||
                                resData.pickupLocation.address}
                            </Text>
                          </Button>
                        </View>

                        <View>
                          <Text type="bodyBold">
                            {resData.customPickupLocation
                              ? 'Dropoff date & time'
                              : 'Pickup date & time'}
                          </Text>

                          <Text>
                            {format(
                              toZonedTime(
                                resData.startTime,
                                resData.pickupLocation.timezone
                              ),
                              'MMM dd - hh:mm a'
                            )}
                          </Text>
                        </View>

                        <View>
                          <Text type="bodyBold">
                            {resData.customPickupLocation
                              ? 'Odometer at dropoff'
                              : 'Odometer at pick up'}
                          </Text>

                          <Text>{resData.startingMileage}</Text>
                        </View>
                      </View>
                    </View>

                    <View
                      style={{
                        borderBottomColor:
                          theme.colors.card.default.borderColor,
                        borderBottomWidth: 1,
                      }}
                    >
                      <View style={{ padding: 16, gap: 4 }}>
                        <View>
                          <Text type="bodyBold">Return location</Text>

                          <Button
                            type="link"
                            onPress={() =>
                              openAddressInMaps(
                                resData.customReturnLocation ||
                                  resData.returnLocation.address
                              )
                            }
                            style={{
                              width: '100%',
                              height: 'auto',
                              paddingTop: 0,
                              paddingRight: 0,
                              paddingBottom: 0,
                              paddingLeft: 0,
                              alignItems: 'flex-start',
                              justifyContent: 'flex-start',
                            }}
                          >
                            <Text
                              type="bodyRegular"
                              style={{ textDecorationLine: 'underline' }}
                            >
                              {resData.customReturnLocation ||
                                resData.returnLocation.address}
                            </Text>
                          </Button>
                        </View>

                        <View>
                          <Text type="bodyBold">Return date & time</Text>

                          <Text>
                            {format(
                              toZonedTime(
                                resData.endTime,
                                resData.returnLocation.timezone
                              ),
                              'MMM dd - hh:mm a'
                            )}
                          </Text>
                        </View>

                        <View>
                          <Text type="bodyBold">Odometer at pick up</Text>

                          <Text>{resData.endingMileage}</Text>
                        </View>
                      </View>
                    </View>

                    {resData?.cancellationPolicy ? (
                      <View
                        style={{
                          borderBottomColor:
                            theme.colors.card.default.borderColor,
                          borderBottomWidth: 1,
                        }}
                      >
                        <View style={{ padding: 16, gap: 4 }}>
                          <Text type="bodyBold">
                            {resData.cancellationPolicy.title} booking
                          </Text>
                        </View>
                      </View>
                    ) : null}

                    <View>
                      <View style={{ padding: 16, gap: 4 }}>
                        <View>
                          <Text type="bodyBold">Miles Allowed</Text>
                          <Text>{`${
                            resData.mileagePlan.totalMilesAllowance === -1
                              ? 'Unlimited'
                              : resData.mileagePlan.totalMilesAllowance
                          } miles`}</Text>
                        </View>

                        <View>
                          <Text type="bodyBold">Miles Driven</Text>
                          <Text>{`${
                            resData.endingMileage - resData.startingMileage
                          } miles`}</Text>
                        </View>

                        <View>
                          <Text type="bodyBold">Excess Miles</Text>
                          <Text>{`${Math.max(
                            resData.extraMiles,
                            0
                          )} miles`}</Text>
                        </View>
                      </View>

                      <View style={{ padding: 16, gap: 4 }}>
                        <View>
                          <Text type="bodyBold">Authorized to drive in:</Text>
                        </View>

                        <View>
                          <Text type="bodyBold">🇺🇸 USA</Text>
                          <Text type="bodyBold">🇨🇦 Canada</Text>
                        </View>
                      </View>
                    </View>
                  </Card>
                </View>

                <View>
                  <Card>
                    <View
                      style={{
                        borderBottomColor:
                          theme.colors.card.default.borderColor,
                        borderBottomWidth: 1,
                      }}
                    >
                      <View style={{ padding: 16 }}>
                        <Text type="title2">Vehicle Information</Text>
                      </View>
                    </View>

                    <View>
                      <View
                        style={{ padding: 16, gap: 4, flexDirection: 'row' }}
                      >
                        <View style={{ width: '40%' }}>
                          <View>
                            <Text type="bodyBold">Brand</Text>
                            <Text>
                              {snakeToTitleCase(resData.car.brandName)}
                            </Text>
                          </View>

                          <View>
                            <Text type="bodyBold">Model</Text>
                            <Text>
                              {snakeToTitleCase(resData.car.modelName)}
                            </Text>
                          </View>

                          <View>
                            <Text type="bodyBold">Year</Text>
                            <Text>
                              {snakeToTitleCase(resData.car.vehicleYear)}
                            </Text>
                          </View>

                          <View>
                            <Text type="bodyBold">Plate</Text>
                            <Text>
                              {snakeToTitleCase(resData.car.licensePlate)}
                            </Text>
                          </View>

                          <View>
                            <Text type="bodyBold">VIN</Text>
                            <Text>{snakeToTitleCase(resData.car.vin)}</Text>
                          </View>
                        </View>

                        <View
                          style={{
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Image
                            source={{
                              uri:
                                resData.car &&
                                getCarImages(resData.car.images).length > 0
                                  ? getCarImages(resData.car.images, 'small')[0]
                                      .url
                                  : '',
                            }}
                            style={{
                              width: isDesktop() ? 300 : 210,
                              aspectRatio: 16 / 9,
                            }}
                            contentFit="cover"
                          />
                        </View>
                      </View>
                    </View>
                  </Card>
                </View>
              </View>

              <View style={{ flexGrow: 1, flexShrink: 1, minWidth: 0 }}>
                <Card style={{ flexGrow: 1 }}>
                  <View
                    style={{
                      borderBottomColor: theme.colors.card.default.borderColor,
                      borderBottomWidth: 1,
                    }}
                  >
                    <View style={{ padding: 16 }}>
                      <Text type="title2">Payment Breakdown</Text>

                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        All charges are subject to final audit.
                      </Text>
                    </View>
                  </View>

                  <View>
                    <View style={{ padding: 16, gap: 24 }}>
                      <View style={{ gap: 8 }}>
                        <View>
                          <Text type="bodyRegular">Rates</Text>

                          <View>
                            {resData.reservationRates.map((rate, index) => (
                              <View
                                key={`rate-${index}`}
                                style={{
                                  width: '100%',
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <View
                                  style={{
                                    width: '33%',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >{`$${formatNumber(rate.dailyRate)}`}</Text>
                                </View>

                                <View
                                  style={{
                                    width: '33%',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >{`x${rate.numDays} days`}</Text>
                                </View>

                                <View
                                  style={{
                                    width: '33%',
                                    alignItems: 'flex-end',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >{`$${formatNumber(
                                    rate.dailyRate * rate.numDays
                                  )}`}</Text>
                                </View>
                              </View>
                            ))}
                          </View>
                        </View>

                        {resData.initiationFee ? (
                          <View
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Text type="bodyRegular">Initiation Fee</Text>

                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              {`$${formatNumber(resData.initiationFee / 100)}`}
                            </Text>
                          </View>
                        ) : null}

                        <View>
                          <Text type="bodyRegular">
                            {resData.insurance.category}
                          </Text>

                          <View
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                            }}
                          >
                            <View
                              style={{
                                width: '33%',
                                alignItems: 'flex-start',
                              }}
                            >
                              <Text
                                type="bodyRegular"
                                style={{ color: theme.colors.text.secondary }}
                              >{`$${formatNumber(
                                resData.insuranceCost / billedDays
                              )}/day`}</Text>
                            </View>

                            <View
                              style={{ width: '33%', alignItems: 'center' }}
                            >
                              <Text
                                type="bodyRegular"
                                style={{ color: theme.colors.text.secondary }}
                              >{`x${billedDays} days`}</Text>
                            </View>

                            <View
                              style={{ width: '33%', alignItems: 'flex-end' }}
                            >
                              <Text
                                type="bodyRegular"
                                style={{ color: theme.colors.text.secondary }}
                              >{`$${formatNumber(
                                resData.insuranceCost
                              )}`}</Text>
                            </View>
                          </View>
                        </View>

                        {resData.totalCancellationPolicyCost > 0 ? (
                          <View
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Text type="bodyRegular">
                              {resData.cancellationPolicy.title} booking
                            </Text>

                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              {`$${formatNumber(
                                resData.totalCancellationPolicyCost
                              )}`}
                            </Text>
                          </View>
                        ) : null}

                        {resData.extras.filter(
                          (e) => e.title !== 'Custom Delivery'
                        ).length > 0 || resData.additionalMilesCost ? (
                          <View>
                            <Text type="bodyRegular">Extras</Text>

                            <View style={{ gap: 8 }}>
                              {resData.extras
                                .filter((e) => e.title !== 'Custom Delivery')
                                .map((extra, index) => (
                                  <View
                                    key={`extra-${index}`}
                                    style={{
                                      flexDirection: 'row',
                                      justifyContent: 'space-between',
                                      gap: 8,
                                    }}
                                  >
                                    <Text
                                      type="bodyRegular"
                                      style={{
                                        color: theme.colors.text.secondary,
                                        flexShrink: 1,
                                      }}
                                    >
                                      {snakeToTitleCase(extra.title)}
                                    </Text>

                                    <Text
                                      type="bodyRegular"
                                      style={{
                                        color: theme.colors.text.secondary,
                                      }}
                                    >
                                      {`$${formatNumber(
                                        extra.recurring
                                          ? extra.amount * resData.numDays
                                          : extra.amount
                                      )}`}
                                    </Text>
                                  </View>
                                ))}

                              {resData.additionalMilesCost ? (
                                <View>
                                  <Text type="bodyRegular">
                                    Additional miles
                                  </Text>

                                  <View
                                    style={{
                                      flexDirection: 'row',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <View
                                      style={{
                                        width: '33%',
                                        alignItems: 'flex-start',
                                      }}
                                    >
                                      <Text
                                        type="bodyRegular"
                                        style={{
                                          color: theme.colors.text.secondary,
                                        }}
                                      >
                                        {`$${formatNumber(
                                          resData.mileagePlan.costPerMile / 100
                                        )} / mile`}
                                      </Text>
                                    </View>

                                    <View
                                      style={{
                                        width: '33%',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Text
                                        type="bodyRegular"
                                        style={{
                                          color: theme.colors.text.secondary,
                                        }}
                                      >
                                        {`x${resData.extraMiles} miles`}
                                      </Text>
                                    </View>

                                    <View
                                      style={{
                                        width: '33%',
                                        alignItems: 'flex-end',
                                      }}
                                    >
                                      <Text
                                        type="bodyRegular"
                                        style={{
                                          color: theme.colors.text.secondary,
                                        }}
                                      >
                                        {`$${formatNumber(
                                          resData.additionalMilesCost
                                        )}`}
                                      </Text>
                                    </View>
                                  </View>
                                </View>
                              ) : null}
                            </View>
                          </View>
                        ) : null}

                        <View>
                          <Text type="bodyRegular">Incidentals</Text>

                          <View style={{ gap: 8 }}>
                            {resData.incidentals.map((incidental, index) => (
                              <View key={`incidental-${index}`}>
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >
                                    {snakeToTitleCase(incidental.category)}
                                  </Text>

                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >
                                    {`$${formatNumber(
                                      incidental.amountWithUpcharge
                                    )}`}
                                  </Text>
                                </View>

                                {incidental.adminFee ? (
                                  <View
                                    style={{
                                      flexDirection: 'row',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <Text
                                      type="bodyRegular"
                                      style={{
                                        color: theme.colors.text.secondary,
                                      }}
                                    >
                                      Admin Fee
                                    </Text>

                                    <Text
                                      type="bodyRegular"
                                      style={{
                                        color: theme.colors.text.secondary,
                                      }}
                                    >
                                      {`$${formatNumber(
                                        incidental.adminFee / 100
                                      )}`}
                                    </Text>
                                  </View>
                                ) : null}
                              </View>
                            ))}
                          </View>
                        </View>

                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text type="bodyRegular">Charging</Text>

                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            {`$${formatNumber(resData.resolvedChargingAmount)}`}
                          </Text>
                        </View>

                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text type="bodyRegular">Tolls</Text>

                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            {`$${formatNumber(resData.resolvedTollsAmount)}`}
                          </Text>
                        </View>

                        {resData.totalDeliveryCost > 0 ? (
                          <View
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Text type="bodyRegular">Delivery Fee</Text>

                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              {`$${formatNumber(resData.totalDeliveryCost)}`}
                            </Text>
                          </View>
                        ) : null}
                      </View>

                      {resData.reservationRates.filter(
                        (rate) => !!rate.discountType
                      ).length > 0 ||
                      resData.discounts.length > 0 ||
                      resData.giftCodes.length > 0 ||
                      resData.adjustments > 0 ? (
                        <View style={{ gap: 4 }}>
                          <Text type="bodyRegular">Discounts</Text>

                          <View style={{ gap: 4 }}>
                            {resData.reservationRates
                              .filter((rate) => !!rate.discountType)
                              .map((rate, index) => (
                                <View
                                  key={`rate-discount-${index}`}
                                  style={{
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{ color: PINKLING }}
                                  >
                                    {rate.discountType}
                                  </Text>

                                  <Text
                                    type="bodyRegular"
                                    style={{ color: PINKLING }}
                                  >
                                    {`-$${formatNumber(rate.discountAmount)}`}
                                  </Text>
                                </View>
                              ))}

                            {resData.discounts.map((discount, index) => (
                              <View
                                key={`discount-${index}`}
                                style={{
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{ color: PINKLING }}
                                >
                                  {discount.title}
                                </Text>

                                <Text
                                  type="bodyRegular"
                                  style={{ color: PINKLING }}
                                >
                                  {`-$${formatNumber(discount.amount)}`}
                                </Text>
                              </View>
                            ))}

                            {resData.giftCodes.map((giftCode, index) => (
                              <View
                                key={`gift-code-${index}`}
                                style={{
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{ color: PINKLING }}
                                >
                                  {giftCode.code}
                                </Text>

                                <Text
                                  type="bodyRegular"
                                  style={{ color: PINKLING }}
                                >
                                  {`-$${formatNumber(giftCode.amount)}`}
                                </Text>
                              </View>
                            ))}

                            {resData.adjustments ? (
                              <View
                                style={{
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{ color: PINKLING }}
                                >
                                  Adjustments
                                </Text>

                                <Text
                                  type="bodyRegular"
                                  style={{ color: PINKLING }}
                                >{`-$${formatNumber(
                                  resData.adjustments
                                )}`}</Text>
                              </View>
                            ) : null}
                          </View>
                        </View>
                      ) : null}

                      {resData.refunds.length > 0 ? (
                        <View style={{ gap: 4 }}>
                          <Text type="bodyRegular">Refunds</Text>

                          <View style={{ gap: 4 }}>
                            {resData.refunds.map((refund, index) => (
                              <View
                                key={`refund-${index}`}
                                style={{
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Text type="bodyRegular">{refund.memo}</Text>

                                <Text
                                  type="bodyRegular"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  {`$${formatNumber(refund.amount)}`}
                                </Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      ) : null}

                      <View style={{ gap: 4 }}>
                        <Text type="bodyRegular">Payments Made</Text>

                        <View style={{ gap: 4 }}>
                          {resData.payments.map((payment, index) => (
                            <View
                              key={`payment-${index}`}
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Text type="bodyRegular">{payment.memo}</Text>

                              <Text
                                type="bodyRegular"
                                style={{ color: theme.colors.text.secondary }}
                              >
                                {`$${formatNumber(payment.amount)}`}
                              </Text>
                            </View>
                          ))}
                        </View>
                      </View>

                      {resData.reservationAdjustments?.map(
                        (adjustment, index) => (
                          <View
                            key={`adjustment-${index}`}
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Text
                              type="bodyRegular"
                              style={{ color: PINKLING }}
                            >
                              {adjustment.adjustmentType ===
                              'non_refundable_shortened_trip'
                                ? 'Shortened Trip'
                                : snakeToTitleCase(adjustment.adjustmentType)}
                            </Text>

                            <Text
                              type="bodyRegular"
                              style={{ color: PINKLING }}
                            >{`${
                              adjustment.amount < 0 ? '-' : ''
                            }$${formatNumber(
                              Math.abs(adjustment.amount)
                            )}`}</Text>
                          </View>
                        )
                      )}

                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text type="bodyRegular">Total cost</Text>

                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          {`$${formatNumber(resData.totalCost)}`}
                        </Text>
                      </View>

                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text type="bodyRegular">Outstanding balance</Text>

                        <Text type="bodyRegular">
                          {`$${formatNumber(resData.outstandingBalance)}`}
                        </Text>
                      </View>
                    </View>
                  </View>
                </Card>
              </View>
            </View>

            <View
              style={{
                backgroundColor: theme.colors.card.default.borderColor,
                height: 1,
              }}
            />

            <View>
              <View style={{ gap: 16 }}>
                <Text type="title1">Financial Responsibility Notice</Text>

                <View style={{ gap: 24 }}>
                  {FINANCIAL_RESPONSIBILITY_PARAGRAPHS.map(
                    (paragraph, index) => (
                      <Text
                        key={`financial-${index}`}
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {paragraph}
                      </Text>
                    )
                  )}
                </View>
              </View>
            </View>

            <View style={styles.signaturesContainer}>
              <Card style={styles.signatureItem}>
                <View
                  style={{
                    borderBottomColor: theme.colors.card.default.borderColor,
                    borderBottomWidth: 1,
                  }}
                >
                  <View style={{ padding: 16 }}>
                    <Text type="title2">Driver Information</Text>
                  </View>
                </View>

                <View style={{ padding: 16, gap: 16 }}>
                  <View>
                    <Text type="bodyRegular">{DRIVER_INFO_NOTICE}</Text>
                  </View>

                  <View>
                    <View>
                      <Text type="bodyBold">Renter (Primary driver)</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {resData.customer.name}
                      </Text>
                    </View>

                    <View>
                      <Text type="bodyBold">Birth Date</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {format(parseISO(resData.customer.dob), 'MM/dd/yyyy')}
                      </Text>
                    </View>

                    <View>
                      <Text type="bodyBold">Email</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {resData.customer.email}
                      </Text>
                    </View>

                    <View>
                      <Text type="bodyBold">Phone</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {resData.customer.phone}
                      </Text>
                    </View>

                    <View>
                      <Text type="bodyBold">Driver License</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {resData.customer.driversLicense}
                      </Text>
                    </View>

                    <View>
                      <Text type="bodyBold">Protection Package</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {resData.insurance.category}
                      </Text>
                    </View>
                  </View>

                  {resData.additionalDrivers.length > 0 ? (
                    <View style={{ gap: 8 }}>
                      {resData.additionalDrivers.map((driver, index) => (
                        <View key={`additional-driver-${index}`}>
                          <View>
                            <Text type="bodyBold">
                              Renter (Secondary driver)
                            </Text>

                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              {driver.name}
                            </Text>
                          </View>

                          <View>
                            <Text type="bodyBold">Birth Date</Text>

                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              {driver.dob
                                ? format(parseISO(driver.dob), 'MM/dd/yyyy')
                                : '-'}
                            </Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  ) : null}
                </View>
              </Card>

              <SignedSignaturesSection
                resData={resData}
                cardStyle={styles.signatureItem}
              />

              <Card style={styles.signatureItem}>
                <View
                  style={{
                    borderBottomColor: theme.colors.card.default.borderColor,
                    borderBottomWidth: 1,
                  }}
                >
                  <View style={{ padding: 16 }}>
                    <Text type="title2">Car Conditions</Text>
                  </View>
                </View>

                <View style={{ justifyContent: 'space-between', flexGrow: 1 }}>
                  <View style={{ flexDirection: 'row', padding: 16 }}>
                    <CheckItem
                      text={CAR_CONDITIONS_CHECKBOX_TEXT}
                      active={resData.acceptedCarConditionsAt !== null}
                      onPress={() => {}}
                      type="checkbox"
                      style={{ flexBasis: 0, flexGrow: 1 }}
                    />
                  </View>

                  <View style={{ flexDirection: 'row', gap: 8, padding: 16 }}>
                    <Text type="caption1">Signature Date</Text>

                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {resData.acceptedCarConditionsAt &&
                        format(
                          toZonedTime(
                            resData.acceptedCarConditionsAt,
                            resData.pickupLocation.timezone
                          ),
                          'yyyy MMM dd - hh:mm a'
                        )}
                    </Text>
                  </View>
                </View>
              </Card>
            </View>

            <View
              style={{
                backgroundColor: theme.colors.card.default.borderColor,
                height: 1,
              }}
            />

            <RentalTermsDetails />
          </View>
        </ScrollView>
      </View>
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    width: '100%',
    height: '100%',
  },
  scrollContent: {
    width: {
      xs: PAGE_WIDTH - 32,
      md: 1000,
    },
    alignSelf: 'center',
    paddingBottom: 16,
  },
  rentalBreakdown: {
    gap: 16,
    flexDirection: {
      md: 'row',
    },
  },
  rentalInformation: {
    gap: 16,
    width: {
      md: '50%',
    },
  },
  signaturesContainer: {
    gap: 16,
    flexDirection: {
      md: 'row',
    },
    flexWrap: 'wrap',
  },
  signatureItem: {
    width: {
      md: '49%',
    },
  },
}));
